// ============================================
// PAGE - Detalle Fiados Cliente
// ============================================
// Detalle de todos los fiados de un cliente específico

import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MdArrowBack,
  MdPerson,
  MdPhone,
  MdCalendarToday,
  MdReceipt,
  MdCheckCircle,
  MdCancel,
  MdPayment,
  MdDelete,
  MdRemoveRedEye,
  MdClose,
  MdEdit,
  MdSwapHoriz,
  MdSearch,
} from 'react-icons/md';
import {
  useDetalleFiadosCliente,
  useTotalesFiadosCliente,
  useEliminarFiadoDetalle,
  useUIStore,
  usePagarFiados,
  usePagarTodoFiados,
  useReasignarFiado,
  useSetNombreClienteAlmacen,
  useDeleteNombreClienteAlmacen,
  useBuscarClientesFiados,
} from '@almacenes/core';
import type { DetalleFiadoCliente, ClienteFiadoResult } from '@almacenes/core';
import { capitalize, formatPhoneNumber, useShepherdTour, TourFab, type TourStepConfig } from '@almacenes/ui';
import { useTourPersistido } from '@/hooks/useTourPersistido';
import logo from '@/assets/logos/nuevo.svg';
import { ModalPagarFiado } from './components/ModalPagarFiado';
import { ModalDetalleFiado } from './components/ModalDetalleFiado';
import { formatearMonto, obtenerValorNumerico } from './formatoMonto';

interface PagoMultiple {
  fiadoId: number;
  boletaId: number;
  fecha: string;
  montoTotal: number;
  saldoPendiente: number;
  montoPagar: number;
  tipoPago: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA';
}

const TOUR_ID = 'fiados-detalle-cliente';
const TOUR_VERSION = 'v1';

interface TourActions {
  abrir: () => void;
  cerrar: () => void;
}

interface BuildDetalleStepsParams {
  hayFiados: boolean;
  hayPendientesEnPagina: boolean;
  hayDeudaPendiente: boolean;
  tourActions: React.MutableRefObject<TourActions>;
}

const buildDetalleSteps = ({
  hayFiados,
  hayPendientesEnPagina,
  hayDeudaPendiente,
  tourActions,
}: BuildDetalleStepsParams): TourStepConfig[] => {
  const steps: TourStepConfig[] = [
    {
      id: 'intro',
      title: 'Fiados de este cliente',
      text: 'Acá ves todas las boletas fiadas de un cliente, cuánto te debe en total y cuánto ya te abonó. Con «Agregar alias» puedes guardarlo con el nombre con el que tú lo conoces.',
      attachTo: { element: '[data-tour="fiados-detalle-header"]', on: 'bottom' },
    },
  ];

  if (hayFiados) {
    steps.push({
      id: 'acciones-fila',
      title: 'Acciones de cada boleta',
      text: 'El ojo muestra el detalle, el ícono de pago abona solo esa boleta, las flechas la traspasan a otro cliente si quedó mal asignada y el basurero la elimina.',
      attachTo: { element: '[data-tour="fiados-detalle-acciones-fila"]', on: 'bottom' },
      waitForSelector: true,
    });
  }

  if (hayPendientesEnPagina) {
    steps.push(
      {
        id: 'seleccion',
        title: 'Marca las boletas que te van a pagar',
        text: 'Marca una o varias boletas pendientes con estos check. El de arriba marca de una vez todas las pendientes de esta página.',
        attachTo: { element: '[data-tour="fiados-detalle-seleccion"]', on: 'bottom' },
      },
      {
        id: 'btn-abonar',
        title: 'Pagar seleccionadas o abonar a la deuda',
        text: 'Con las boletas marcadas, este botón abre el pago en conjunto: puedes pagarlas completas o repartir un abono parcial entre ellas.',
        attachTo: { element: '[data-tour="fiados-detalle-btn-abonar"]', on: 'bottom' },
      },
      {
        id: 'monto-abonar',
        title: 'El abono se reparte solo',
        text: 'Escribe cuánta plata te está pagando el cliente y el sistema la reparte empezando por la boleta más antigua. Si el monto alcanza para más boletas pendientes de esta página, las agrega automáticamente a la lista.',
        attachTo: { element: '[data-tour="fiados-detalle-monto-abonar"]', on: 'bottom' },
        beforeShow: () => tourActions.current.abrir(),
        waitForSelector: true,
      },
      {
        id: 'confirmar-pagos',
        title: 'Que no quede plata sin asignar',
        text: 'Acá ves el total a pagar y cuánto quedó «Sin asignar». Mientras eso no esté en $0, el botón Confirmar Pagos queda bloqueado. Igual puedes editar a mano el monto y el método de pago de cada boleta.',
        attachTo: { element: '[data-tour="fiados-detalle-resumen-pago"]', on: 'top' },
        beforeShow: () => tourActions.current.abrir(),
        waitForSelector: true,
      }
    );
  }

  if (hayDeudaPendiente) {
    steps.push({
      id: 'pagar-todo',
      title: 'Pagar toda la deuda',
      text: 'Si el cliente te paga todo de una, este botón salda todas sus boletas pendientes con el método que elijas, sin tener que marcarlas una por una.',
      attachTo: { element: '[data-tour="fiados-detalle-btn-pagar-todo"]', on: 'bottom' },
      beforeShow: () => tourActions.current.cerrar(),
      waitForSelector: true,
    });
  }

  return steps;
};

export const DetalleFiadosClientePage = () => {
  const { clienteAlmacenId } = useParams<{ clienteAlmacenId: string }>();
  const navigate = useNavigate();
  const addToast = useUIStore((state) => state.addToast);

  const [pagina, setPagina] = useState(0);
  const [modalEliminar, setModalEliminar] = useState<{ open: boolean; fiadoId: number | null }>({
    open: false,
    fiadoId: null,
  });
  const [modalPagar, setModalPagar] = useState<{ open: boolean; fiado: DetalleFiadoCliente | null }>({
    open: false,
    fiado: null,
  });
  const [modalDetalle, setModalDetalle] = useState<{ open: boolean; fiado: DetalleFiadoCliente | null }>({
    open: false,
    fiado: null,
  });
  const [fiadosSeleccionados, setFiadosSeleccionados] = useState<number[]>([]);
  const [modalPagoMultiple, setModalPagoMultiple] = useState(false);
  const [pagosMultiples, setPagosMultiples] = useState<PagoMultiple[]>([]);
  const [montoAbonarInput, setMontoAbonarInput] = useState('');
  const [modalPagarTodo, setModalPagarTodo] = useState(false);
  const [tipoPagoTodo, setTipoPagoTodo] = useState<'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA'>('EFECTIVO');

  // Estado alias
  const [aliasInput, setAliasInput] = useState('');
  const [aliasOriginal, setAliasOriginal] = useState('');
  const [editandoAlias, setEditandoAlias] = useState(false);

  // Estado reasignación
  const [modalReasignar, setModalReasignar] = useState<{ open: boolean; fiadoId: number | null }>({ open: false, fiadoId: null });
  const [busquedaReasignar, setBusquedaReasignar] = useState('');
  const [resultadosReasignar, setResultadosReasignar] = useState<ClienteFiadoResult[]>([]);

  // Queries
  const clienteId = parseInt(clienteAlmacenId || '0');
  const { data, isLoading, error } = useDetalleFiadosCliente(clienteId, pagina);
  const { data: totales } = useTotalesFiadosCliente(clienteId);
  const { mutate: eliminarFiado, isPending: isEliminando } = useEliminarFiadoDetalle(clienteId, pagina);
  const { mutate: pagarFiados, isPending: isPagando } = usePagarFiados();
  const { mutate: pagarTodo, isPending: isPagandoTodo } = usePagarTodoFiados(clienteId);
  const { mutate: reasignarFiado, isPending: isReasignando } = useReasignarFiado(clienteId);
  const { mutate: setAlias, isPending: isGuardandoAlias } = useSetNombreClienteAlmacen(clienteId);
  const { mutate: deleteAlias, isPending: isDeletingAlias } = useDeleteNombreClienteAlmacen(clienteId);
  const buscarClientesMutation = useBuscarClientesFiados();

  // Datos del cliente: se obtiene del primer registro cargado y se mantiene
  // aunque cambie la página (para no perder el encabezado mientras se pagina).
  const clienteInfo = data?.content.find((f) => f.nombreCliente) ?? data?.content[0];

  // Sincronizar alias cuando cambian los datos del servidor (solo si no está en edición)
  useEffect(() => {
    if (!editandoAlias) {
      const aliasActual = clienteInfo?.aliasNombre ?? '';
      setAliasOriginal(aliasActual);
      setAliasInput(aliasActual);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteInfo?.aliasNombre]);

  const handleGuardarAlias = () => {
    const nuevoAlias = aliasInput.trim();
    if (nuevoAlias === '') {
      deleteAlias(undefined, {
        onSuccess: () => {
          setAliasOriginal('');
          setEditandoAlias(false);
          addToast('success', 'Alias eliminado');
        },
        onError: () => addToast('error', 'Error al eliminar el alias'),
      });
    } else {
      setAlias(nuevoAlias, {
        onSuccess: () => {
          setAliasOriginal(nuevoAlias);
          setEditandoAlias(false);
          addToast('success', 'Alias guardado');
        },
        onError: () => addToast('error', 'Error al guardar el alias'),
      });
    }
  };

  const handleBuscarParaReasignar = async () => {
    if (busquedaReasignar.length < 3) return;
    try {
      const resultados = await buscarClientesMutation.mutateAsync({ nombre: busquedaReasignar });
      setResultadosReasignar(resultados);
    } catch {
      addToast('error', 'Error al buscar clientes');
    }
  };

  const handleConfirmarReasignacion = (clienteDestino: ClienteFiadoResult) => {
    if (!modalReasignar.fiadoId || !clienteDestino.clienteAlmacenId) return;
    reasignarFiado(
      { fiadoId: modalReasignar.fiadoId, request: { clienteAlmacenId: clienteDestino.clienteAlmacenId } },
      {
        onSuccess: () => {
          setModalReasignar({ open: false, fiadoId: null });
          setBusquedaReasignar('');
          setResultadosReasignar([]);
          addToast('success', 'Fiado reasignado exitosamente');
        },
        onError: () => addToast('error', 'Error al reasignar el fiado'),
      }
    );
  };

  // Handlers
  const handlePagina = (nuevaPagina: number) => {
    setPagina(nuevaPagina);
  };

  const handleAbrirModalEliminar = (fiadoId: number) => {
    setModalEliminar({ open: true, fiadoId });
  };

  const handleCerrarModalEliminar = () => {
    setModalEliminar({ open: false, fiadoId: null });
  };

  const handleConfirmarEliminar = () => {
    if (modalEliminar.fiadoId) {
      eliminarFiado(modalEliminar.fiadoId, {
        onSuccess: () => {
          handleCerrarModalEliminar();
          addToast('success', 'Fiado eliminado exitosamente');
          
          // Si ya no quedan fiados, volver a la página anterior
          if (data && data.content.length === 1) {
            navigate('/fiados');
          }
        },
        onError: (error) => {
          console.error('Error al eliminar fiado:', error);
          addToast('error', 'Error al eliminar el fiado. Por favor, intente nuevamente.');
        },
      });
    }
  };

  const handlePagar = (fiado: DetalleFiadoCliente) => {
    // Convertir DetalleFiadoCliente a formato FiadoHistorico
    const fiadoParaPagar = {
      fiadoId: fiado.id,
      clienteAlmacenId: clienteId,
      clienteId: 0, // No lo tenemos pero no es necesario para el modal
      boletaId: fiado.boletaId,
      clienteNombre: fiado.aliasNombre || fiado.nombreCliente,
      clienteTelefono: fiado.telefonoCliente,
      clienteRut: null,
      clienteDv: null,
      fecha: fiado.fechaHoraTransaccion,
      pagado: fiado.pagado,
      montoAbonado: fiado.abonoFiado,
      montoTotal: fiado.monto,
      saldoPendiente: fiado.saldoPendiente,
    };
    setModalPagar({ open: true, fiado: fiadoParaPagar as any });
  };

  const handleCerrarModalPagar = () => {
    setModalPagar({ open: false, fiado: null });
  };

  const handleConfirmarPago = (tipoPago: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA', monto: number) => {
    if (!modalPagar.fiado) return;

    pagarFiados(
      {
        pagos: [
          {
            idFiado: (modalPagar.fiado as any).fiadoId,
            tipoPago,
            monto,
          },
        ],
      },
      {
        onSuccess: () => {
          handleCerrarModalPagar();
          addToast('success', 'Pago registrado exitosamente');
        },
        onError: (error) => {
          console.error('Error al pagar fiado:', error);
          addToast('error', 'Error al registrar el pago. Por favor, intente nuevamente.');
        },
      }
    );
  };

  const handleVerDetalle = (fiado: DetalleFiadoCliente) => {
    // Convertir DetalleFiadoCliente a formato FiadoHistorico
    const fiadoParaDetalle = {
      fiadoId: fiado.id,
      clienteAlmacenId: clienteId,
      clienteId: 0,
      boletaId: fiado.boletaId,
      clienteNombre: fiado.aliasNombre || fiado.nombreCliente,
      clienteTelefono: fiado.telefonoCliente,
      clienteRut: null,
      clienteDv: null,
      fecha: fiado.fechaHoraTransaccion,
      pagado: fiado.pagado,
      montoAbonado: fiado.abonoFiado,
      montoTotal: fiado.monto,
      saldoPendiente: fiado.saldoPendiente,
    };
    setModalDetalle({ open: true, fiado: fiadoParaDetalle as any });
  };

  const handleCerrarModalDetalle = () => {
    setModalDetalle({ open: false, fiado: null });
  };

  const handleSeleccionarFiado = (fiadoId: number) => {
    setFiadosSeleccionados((prev) => {
      if (prev.includes(fiadoId)) {
        return prev.filter((id) => id !== fiadoId);
      }
      return [...prev, fiadoId];
    });
  };

  const handleSeleccionarTodos = () => {
    if (!data) return;
    const fiadosPendientes = data.content.filter((f) => !f.pagado).map((f) => f.id);
    
    if (fiadosSeleccionados.length === fiadosPendientes.length) {
      setFiadosSeleccionados([]);
    } else {
      setFiadosSeleccionados(fiadosPendientes);
    }
  };

  // Construye pagosMultiples a partir de una lista de ids y abre el modal. Extraído de
  // handleAbrirPagoMultiple para poder reusarlo desde el tour (ver abrirPagoMultipleDemoTour).
  const abrirPagoMultipleCon = (ids: number[]) => {
    if (!data) return;

    // Del más antiguo al más nuevo: así el cajero ve de un vistazo cuál conviene
    // saldar primero, y es el mismo orden que usa la distribución de "Monto a abonar".
    const fiadosAPagar = data.content
      .filter((f) => ids.includes(f.id))
      .sort((a, b) => new Date(a.fechaHoraTransaccion).getTime() - new Date(b.fechaHoraTransaccion).getTime())
      .map((f) => ({
        fiadoId: f.id,
        boletaId: f.boletaId,
        fecha: f.fechaHoraTransaccion,
        montoTotal: f.monto,
        saldoPendiente: f.saldoPendiente,
        montoPagar: f.saldoPendiente,
        tipoPago: 'EFECTIVO' as const,
      }));

    setPagosMultiples(fiadosAPagar);
    setMontoAbonarInput('');
    setModalPagoMultiple(true);
  };

  const handleAbrirPagoMultiple = () => abrirPagoMultipleCon(fiadosSeleccionados);

  const handleCerrarPagoMultiple = () => {
    setModalPagoMultiple(false);
    setPagosMultiples([]);
    setMontoAbonarInput('');
  };

  // --- solo para el tour guiado ---
  // Abre el modal con una selección de ejemplo si el cajero no marcó nada, para que los
  // steps del modal no queden mostrando una pantalla vacía.
  const abrirPagoMultipleDemoTour = () => {
    if (!data) return;
    const ids = fiadosSeleccionados.length > 0
      ? fiadosSeleccionados
      : data.content.filter((f) => !f.pagado).slice(0, 2).map((f) => f.id);
    if (ids.length === 0) return;
    setFiadosSeleccionados(ids);
    abrirPagoMultipleCon(ids);
  };

  const cerrarPagoMultipleDemoTour = () => {
    handleCerrarPagoMultiple();
    setFiadosSeleccionados([]);
  };

  // Ref estable: evita que `steps` cambie de identidad en cada render (cambiar `steps`
  // reconstruye el tour y su cleanup lo cancela a mitad de camino — ver useShepherdTour).
  const tourActionsRef = useRef<TourActions>({ abrir: abrirPagoMultipleDemoTour, cerrar: cerrarPagoMultipleDemoTour });
  tourActionsRef.current.abrir = abrirPagoMultipleDemoTour;
  tourActionsRef.current.cerrar = cerrarPagoMultipleDemoTour;

  const handleActualizarMontoPago = (fiadoId: number, monto: number) => {
    setPagosMultiples((prev) =>
      prev.map((p) => (p.fiadoId === fiadoId ? { ...p, montoPagar: monto } : p))
    );
  };

  // Distribuye "Monto a abonar" entre los fiados ya seleccionados (del más antiguo al más
  // nuevo) y, si sobra, va sumando fiados pendientes de la misma página que aún no estaban
  // marcados — para que el cajero no tenga que calcular a mano cuántos fiados cubre un monto.
  // Solo alcanza los fiados ya cargados en la página actual del listado (limitación heredada:
  // los checkboxes tampoco cruzan páginas).
  const handleMontoAbonarChange = (valorInput: string) => {
    const formateado = formatearMonto(valorInput);
    setMontoAbonarInput(formateado);
    const monto = obtenerValorNumerico(formateado);
    if (monto <= 0 || !data) return;

    const seleccionadosIds = new Set(pagosMultiples.map((p) => p.fiadoId));
    const seleccionadosOrdenados = [...pagosMultiples].sort(
      (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
    );
    const sugeridosOrdenados = data.content
      .filter((f) => !f.pagado && !seleccionadosIds.has(f.id))
      .sort((a, b) => new Date(a.fechaHoraTransaccion).getTime() - new Date(b.fechaHoraTransaccion).getTime());

    let restante = monto;
    const nuevosPagos: PagoMultiple[] = [];
    const nuevosSeleccionados: number[] = [];

    for (const p of seleccionadosOrdenados) {
      const aplicado = Math.min(p.saldoPendiente, Math.max(restante, 0));
      nuevosPagos.push({ ...p, montoPagar: aplicado });
      nuevosSeleccionados.push(p.fiadoId);
      restante -= aplicado;
    }

    for (const f of sugeridosOrdenados) {
      if (restante <= 0) break;
      const aplicado = Math.min(f.saldoPendiente, restante);
      nuevosPagos.push({
        fiadoId: f.id,
        boletaId: f.boletaId,
        fecha: f.fechaHoraTransaccion,
        montoTotal: f.monto,
        saldoPendiente: f.saldoPendiente,
        montoPagar: aplicado,
        tipoPago: 'EFECTIVO',
      });
      nuevosSeleccionados.push(f.id);
      restante -= aplicado;
    }

    setPagosMultiples(nuevosPagos);
    setFiadosSeleccionados(nuevosSeleccionados);
  };

  const handleActualizarTipoPago = (fiadoId: number, tipoPago: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA') => {
    setPagosMultiples((prev) =>
      prev.map((p) => (p.fiadoId === fiadoId ? { ...p, tipoPago } : p))
    );
  };

  const montoAbonarObjetivo = obtenerValorNumerico(montoAbonarInput);
  const totalAPagarMultiple = pagosMultiples.reduce((acc, p) => acc + p.montoPagar, 0);
  const sinAsignarMultiple = montoAbonarObjetivo - totalAPagarMultiple;
  // El reparto automático ya intentó sumar todos los pendientes de esta página (ver
  // handleMontoAbonarChange); si igual sobra monto y no quedan más pendientes sin marcar,
  // es porque hay más fiados del cliente en otra página que el reparto no puede alcanzar.
  const pendientesEnPagina = data?.content.filter((f) => !f.pagado).length ?? 0;
  const topeDePaginaAlcanzado = sinAsignarMultiple > 0 && pagosMultiples.length >= pendientesEnPagina;

  const handleConfirmarPagoMultiple = () => {
    const pagosValidos = pagosMultiples.filter((p) => p.montoPagar > 0);

    if (pagosValidos.length === 0) {
      addToast('error', 'Debe ingresar al menos un monto válido');
      return;
    }

    // Si el cajero usó "Monto a abonar", exigimos que la suma cuadre exacto con ese objetivo
    // antes de dejarlo confirmar (mismo criterio que valida el botón, doble chequeo por si
    // llega a habilitarse el botón con un estado intermedio).
    if (montoAbonarObjetivo > 0 && sinAsignarMultiple !== 0) {
      addToast('error', 'La suma de los montos no coincide con el monto a abonar ingresado');
      return;
    }

    pagarFiados(
      {
        pagos: pagosValidos.map((p) => ({
          idFiado: p.fiadoId,
          tipoPago: p.tipoPago,
          monto: p.montoPagar,
        })),
      },
      {
        onSuccess: () => {
          handleCerrarPagoMultiple();
          setFiadosSeleccionados([]);
          addToast('success', 'Pagos registrados exitosamente');
        },
        onError: (error) => {
          console.error('Error al pagar fiados:', error);
          addToast('error', 'Error al registrar los pagos. Por favor, intente nuevamente.');
        },
      }
    );
  };

  const handleAbrirPagarTodo = () => {
    setTipoPagoTodo('EFECTIVO');
    setModalPagarTodo(true);
  };

  const handleCerrarPagarTodo = () => {
    setModalPagarTodo(false);
  };

  const handleConfirmarPagarTodo = () => {
    pagarTodo(tipoPagoTodo, {
      onSuccess: () => {
        handleCerrarPagarTodo();
        addToast('success', 'Toda la deuda fue pagada exitosamente');
      },
      onError: () => {
        addToast('error', 'Error al pagar la deuda. Por favor, intente nuevamente.');
      },
    });
  };

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalFiados = totales?.totalFiados ?? data?.totalElements ?? 0;
  const totalPendiente = totales?.saldoPendienteTotal ?? 0;
  const totalAbonado = totales?.totalAbonado ?? 0;

  // Ancla única del tour dentro del .map(): la primera fila pendiente (la única con los 4
  // botones visibles), para que [data-tour="..."] no matchee N filas.
  const filaTourId = data?.content.find((f) => !f.pagado)?.id ?? data?.content[0]?.id;

  const hayFiados = (data?.content.length ?? 0) > 0;
  const hayPendientesEnPagina = data?.content.some((f) => !f.pagado) ?? false;
  const hayDeudaPendiente = totalPendiente > 0;

  const steps = useMemo(
    () => buildDetalleSteps({ hayFiados, hayPendientesEnPagina, hayDeudaPendiente, tourActions: tourActionsRef }),
    [hayFiados, hayPendientesEnPagina, hayDeudaPendiente]
  );
  const { start } = useShepherdTour(steps, { logoUrl: logo, name: 'Almacenes Digitales' });
  const { seen, markAsSeen } = useTourPersistido(TOUR_ID, TOUR_VERSION);

  useEffect(() => {
    if (!seen && !isLoading) {
      start();
      markAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen, isLoading]);

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      <TourFab onClick={start} />
      {/* Header */}
      <div className="mb-6" data-tour="fiados-detalle-header">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate('/fiados')}
            className="text-[#43BFFF] hover:text-[#2BA5E8] transition-colors p-2 hover:bg-blue-50 rounded-lg"
            title="Volver a Fiados"
          >
            <MdArrowBack className="text-2xl" />
          </button>
          <h1 className="text-3xl font-bold text-[#11194F]">Detalle de Fiados</h1>
        </div>
        
        {clienteInfo && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-gray-700">
              <MdPerson className="text-gray-400" />
              <span className="font-medium">{capitalize(clienteInfo.aliasNombre ?? clienteInfo.nombreCliente)}</span>
              {clienteInfo.aliasNombre && (
                <span className="text-xs text-gray-400">({capitalize(clienteInfo.nombreCliente)})</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <MdPhone className="text-gray-400" />
              <span>{formatPhoneNumber(clienteInfo.telefonoCliente)}</span>
            </div>
            {/* Sección alias */}
            <div className="flex items-center gap-2 mt-1">
              {editandoAlias ? (
                <>
                  <input
                    type="text"
                    value={aliasInput}
                    onChange={(e) => setAliasInput(e.target.value)}
                    placeholder={capitalize(clienteInfo.nombreCliente)}
                    maxLength={255}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none w-48"
                  />
                  <button
                    onClick={handleGuardarAlias}
                    disabled={aliasInput.trim() === aliasOriginal || isGuardandoAlias || isDeletingAlias}
                    className="px-3 py-1 bg-[#43BFFF] text-white rounded-lg text-sm hover:bg-[#2BA5E8] disabled:opacity-50 transition-colors"
                  >
                    Guardar
                  </button>
                  <button
                    onClick={() => { setAliasInput(aliasOriginal); setEditandoAlias(false); }}
                    className="px-3 py-1 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditandoAlias(true)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#43BFFF] transition-colors"
                >
                  <MdEdit className="text-sm" />
                  {aliasOriginal ? 'Editar alias' : 'Agregar alias'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Resumen */}
      {data && data.content.length > 0 && (
        <div className="bg-white rounded-xl shadow mb-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Fiados</p>
              <p className="text-2xl font-bold text-[#43BFFF]">{totalFiados}</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Saldo Pendiente Total</p>
              <p className="text-2xl font-bold text-red-600">${totalPendiente.toLocaleString('es-CL')}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Abonado</p>
              <p className="text-2xl font-bold text-green-600">
                ${totalAbonado.toLocaleString('es-CL')}
              </p>
            </div>
          </div>
          {totalPendiente > 0 && (
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={handleAbrirPagoMultiple}
                disabled={fiadosSeleccionados.length === 0}
                title={fiadosSeleccionados.length === 0 ? 'Selecciona al menos un fiado' : undefined}
                data-tour="fiados-detalle-btn-abonar"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-colors font-semibold text-sm shadow ${
                  fiadosSeleccionados.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#43BFFF] text-white hover:bg-[#2BA5E8]'
                }`}
              >
                <MdPayment className="text-lg" />
                Pagar Seleccionadas o Abonar a Deuda
                {fiadosSeleccionados.length > 0 && ` (${fiadosSeleccionados.length})`}
              </button>
              <button
                onClick={handleAbrirPagarTodo}
                data-tour="fiados-detalle-btn-pagar-todo"
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-sm shadow"
              >
                <MdPayment className="text-lg" />
                Pagar toda la deuda
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F3F4F9]">
              <tr>
                <th className="px-6 py-4 text-center text-sm font-bold text-[#11194F]" data-tour="fiados-detalle-seleccion">
                  <input
                    type="checkbox"
                    checked={data?.content.filter((f) => !f.pagado).length === fiadosSeleccionados.length && fiadosSeleccionados.length > 0}
                    onChange={handleSeleccionarTodos}
                    className="w-5 h-5 text-[#43BFFF] rounded focus:ring-[#43BFFF]"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">N° Boleta</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Fecha</th>
                <th className="px-6 py-4 text-center text-sm font-bold text-[#11194F]">Estado</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Total</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Abonado</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Pendiente</th>
                <th className="px-6 py-4 text-center text-sm font-bold text-[#11194F]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <div className="flex justify-center">
                      <div className="w-8 h-8 border-4 border-[#43BFFF] border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="mt-4 text-gray-600">Cargando fiados...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-red-500">
                    Error al cargar los fiados. Por favor, intente nuevamente.
                  </td>
                </tr>
              ) : data && data.content.length > 0 ? (
                data.content.map((fiado, index) => (
                  <tr key={fiado.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 text-center">
                      {!fiado.pagado && (
                        <input
                          type="checkbox"
                          checked={fiadosSeleccionados.includes(fiado.id)}
                          onChange={() => handleSeleccionarFiado(fiado.id)}
                          className="w-5 h-5 text-[#43BFFF] rounded focus:ring-[#43BFFF]"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <MdReceipt className="text-gray-400" />
                        <span className="text-sm font-semibold text-[#11194F]">#{fiado.boletaId}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <MdCalendarToday className="text-gray-400 text-sm" />
                        <span className="text-sm text-gray-900">{formatearFecha(fiado.fechaHoraTransaccion)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {fiado.pagado ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <MdCheckCircle />
                          Pagado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          <MdCancel />
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      ${fiado.monto.toLocaleString('es-CL')}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-green-600">
                      ${fiado.abonoFiado.toLocaleString('es-CL')}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-red-600">
                      ${fiado.saldoPendiente.toLocaleString('es-CL')}
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="flex items-center justify-center gap-2"
                        data-tour={fiado.id === filaTourId ? 'fiados-detalle-acciones-fila' : undefined}
                      >
                        <button
                          onClick={() => handleVerDetalle(fiado)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver detalle"
                        >
                          <MdRemoveRedEye className="text-xl" />
                        </button>
                        {!fiado.pagado ? (
                          <>
                            <button
                              onClick={() => handlePagar(fiado)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Pagar fiado"
                            >
                              <MdPayment className="text-xl" />
                            </button>
                            <button
                              onClick={() => { setModalReasignar({ open: true, fiadoId: fiado.id }); setBusquedaReasignar(''); setResultadosReasignar([]); }}
                              className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Cambiar cliente"
                            >
                              <MdSwapHoriz className="text-xl" />
                            </button>
                            <button
                              onClick={() => handleAbrirModalEliminar(fiado.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar fiado"
                            >
                              <MdDelete className="text-xl" />
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-500">
                    No se encontraron fiados para este cliente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {data && data.content.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                Mostrando {data.content.length} de {data.totalElements} fiados
              </p>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePagina(pagina - 1)}
                  disabled={pagina === 0}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: data.totalPages }, (_, i) => i).map((page) => {
                    if (
                      page === 0 ||
                      page === data.totalPages - 1 ||
                      (page >= pagina - 1 && page <= pagina + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePagina(page)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            page === pagina
                              ? 'bg-[#43BFFF] text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {page + 1}
                        </button>
                      );
                    } else if (page === pagina - 2 || page === pagina + 2) {
                      return <span key={page} className="px-2 text-gray-400">...</span>;
                    }
                    return null;
                  })}
                </div>
                
                <button
                  onClick={() => handlePagina(pagina + 1)}
                  disabled={pagina >= data.totalPages - 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmación para eliminar fiado */}
      {modalEliminar.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <MdDelete className="text-2xl text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#11194F]">Eliminar Fiado</h3>
                  <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                ¿Está seguro que desea eliminar este fiado? Se eliminará permanentemente del sistema.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleCerrarModalEliminar}
                  disabled={isEliminando}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarEliminar}
                  disabled={isEliminando}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isEliminando ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <MdDelete />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de pago múltiple */}
      {modalPagoMultiple && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-[#F3F4F9]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-[#11194F]">Pagar Deudas Seleccionadas</h3>
                  <p className="text-sm text-gray-600 mt-1">{pagosMultiples.length} fiado{pagosMultiples.length > 1 ? 's' : ''} seleccionado{pagosMultiples.length > 1 ? 's' : ''}</p>
                </div>
                <button
                  onClick={handleCerrarPagoMultiple}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <MdClose className="text-2xl text-gray-600" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Monto a abonar (opcional): si se usa, reparte automáticamente del fiado más
                  antiguo al más nuevo entre los seleccionados, y suma más fiados pendientes de
                  esta misma página si el monto alcanza para más de lo ya marcado. */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl border-2 border-gray-200" data-tour="fiados-detalle-monto-abonar">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto a abonar (opcional)
                </label>
                <div className="relative max-w-xs">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold">
                    $
                  </span>
                  <input
                    type="text"
                    value={montoAbonarInput}
                    onChange={(e) => handleMontoAbonarChange(e.target.value)}
                    placeholder="0"
                    className="w-full pl-7 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#43BFFF]"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Ingresa un monto y se reparte solo entre los fiados más antiguos; si sobra y hay
                  más fiados pendientes en esta página, se agregan automáticamente.
                </p>
              </div>

              <div className="space-y-4">
                {pagosMultiples.map((pago, index) => (
                  <div key={pago.fiadoId} className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-600">Boleta #{pago.boletaId}</p>
                          {index === 0 && pagosMultiples.length > 1 && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-[#43BFFF] rounded-full font-medium">
                              más antigua
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{formatearFecha(pago.fecha)}</p>
                        <p className="text-lg font-bold text-[#11194F]">
                          Saldo Pendiente: ${pago.saldoPendiente.toLocaleString('es-CL')}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Monto a pagar */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Monto a Pagar
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold">
                            $
                          </span>
                          <input
                            type="text"
                            value={pago.montoPagar === 0 ? '' : formatearMonto(String(pago.montoPagar))}
                            onChange={(e) => handleActualizarMontoPago(pago.fiadoId, obtenerValorNumerico(e.target.value))}
                            placeholder="0"
                            className="w-full pl-7 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#43BFFF]"
                          />
                        </div>
                        {pago.montoPagar > pago.saldoPendiente && (
                          <p className="text-xs text-red-500 mt-1">Supera el saldo pendiente</p>
                        )}
                      </div>

                      {/* Tipo de pago */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Método de Pago
                        </label>
                        <select
                          value={pago.tipoPago}
                          onChange={(e) => handleActualizarTipoPago(pago.fiadoId, e.target.value as any)}
                          className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#43BFFF]"
                        >
                          <option value="EFECTIVO">Efectivo</option>
                          <option value="DEBITO">Débito</option>
                          <option value="CREDITO">Crédito</option>
                          <option value="TRANSFERENCIA">Transferencia</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Resumen */}
              <div className="mt-6 p-4 bg-[#43BFFF] bg-opacity-10 rounded-xl border-2 border-[#43BFFF]" data-tour="fiados-detalle-resumen-pago">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-[#11194F]">Total a Pagar:</span>
                  <span className="text-2xl font-bold text-[#43BFFF]">
                    ${totalAPagarMultiple.toLocaleString('es-CL')}
                  </span>
                </div>
                {montoAbonarObjetivo > 0 && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#43BFFF] border-opacity-30">
                    <span className="text-sm text-gray-600">Sin asignar del monto a abonar:</span>
                    <span className={`text-sm font-bold ${sinAsignarMultiple === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${sinAsignarMultiple.toLocaleString('es-CL')}
                    </span>
                  </div>
                )}
                {topeDePaginaAlcanzado && (
                  <p className="text-xs text-gray-500 mt-2">
                    Si el cliente tiene más fiados pendientes en otra página, ciérralo y ajusta la
                    selección manualmente.
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={handleCerrarPagoMultiple}
                  disabled={isPagando}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 font-semibold"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarPagoMultiple}
                  disabled={isPagando || (montoAbonarObjetivo > 0 && sinAsignarMultiple !== 0)}
                  className="flex-1 px-4 py-3 bg-[#10B981] text-white rounded-xl hover:bg-[#059669] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                >
                  {isPagando ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <MdPayment className="text-xl" />
                      Confirmar Pagos
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pagar toda la deuda */}
      {modalPagarTodo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <MdPayment className="text-2xl text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#11194F]">Pagar toda la deuda</h3>
                    <p className="text-sm text-gray-600">
                      {totalFiados} fiado{totalFiados !== 1 ? 's' : ''} · ${totalPendiente.toLocaleString('es-CL')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCerrarPagarTodo}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                  aria-label="Cerrar modal"
                >
                  <MdClose className="text-xl text-gray-500" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Se registrará el pago total de todos los fiados pendientes con el método seleccionado.
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago</label>
                <select
                  value={tipoPagoTodo}
                  onChange={(e) => setTipoPagoTodo(e.target.value as typeof tipoPagoTodo)}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#43BFFF] text-gray-800"
                >
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="DEBITO">Débito</option>
                  <option value="CREDITO">Crédito</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCerrarPagarTodo}
                  disabled={isPagandoTodo}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarPagarTodo}
                  disabled={isPagandoTodo}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isPagandoTodo ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <MdPayment />
                      Confirmar pago
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de pago */}
      {modalPagar.open && modalPagar.fiado && (
        <ModalPagarFiado
          fiado={modalPagar.fiado as any}
          onClose={handleCerrarModalPagar}
          onPagar={handleConfirmarPago}
          isPaying={isPagando}
        />
      )}

      {/* Modal de detalle */}
      {modalDetalle.open && modalDetalle.fiado && (
        <ModalDetalleFiado
          fiado={modalDetalle.fiado as any}
          onClose={handleCerrarModalDetalle}
        />
      )}

      {/* Modal reasignación de cliente */}
      {modalReasignar.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <MdSwapHoriz className="text-xl text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#11194F]">Cambiar Cliente del Fiado</h3>
                </div>
                <button onClick={() => { setModalReasignar({ open: false, fiadoId: null }); setBusquedaReasignar(''); setResultadosReasignar([]); }} className="p-1 hover:bg-gray-100 rounded-lg" aria-label="Cerrar modal">
                  <MdClose className="text-xl text-gray-500" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">Busca el cliente correcto por nombre, teléfono o RUT.</p>

              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={busquedaReasignar}
                  onChange={(e) => setBusquedaReasignar(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleBuscarParaReasignar()}
                  placeholder="Nombre, teléfono o RUT..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                />
                <button
                  onClick={handleBuscarParaReasignar}
                  disabled={busquedaReasignar.length < 3 || buscarClientesMutation.isPending}
                  className="px-4 py-2 bg-[#43BFFF] text-white rounded-lg text-sm hover:bg-[#2BA5E8] disabled:opacity-50 transition-colors flex items-center gap-1"
                >
                  <MdSearch />
                  Buscar
                </button>
              </div>

              {resultadosReasignar.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {resultadosReasignar.map((cliente) => (
                    <button
                      key={cliente.clienteId}
                      onClick={() => handleConfirmarReasignacion(cliente)}
                      disabled={isReasignando}
                      className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#43BFFF] hover:bg-blue-50 transition-colors disabled:opacity-50"
                    >
                      <p className="font-medium text-gray-900 text-sm">{capitalize(cliente.alias || cliente.nombre)}</p>
                      <p className="text-xs text-gray-500">Tel: {cliente.telefono}{cliente.rut ? ` · RUT: ${cliente.rut}-${cliente.dv}` : ''}</p>
                    </button>
                  ))}
                </div>
              )}

              {buscarClientesMutation.isSuccess && resultadosReasignar.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No se encontraron clientes</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
